/* eslint-disable max-len */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import {
  CCard, CCardBody, CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell,
  CPagination, CPaginationItem, CTooltip, CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
  CButton, CBadge, CRow, CCol, CFormInput, CCollapse
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilPencil, cilTrash, cilFilter, cilX, cilSearch, cilArrowTop, cilArrowBottom } from '@coreui/icons';
import { mainColors } from '../../helpers/colors';
import {
  deleteStockRequest, getStockRequest
} from '../../store/stock/stockActions';
import './StockTable.scss';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import ExportData from '../ExportData/ExportData';

function StockTable(props) {
  const {
    stockList,
    isLoading,
    deleteStockRequest,
    // Sayfalama alanları (Redux'dan)
    totalCount,
    pageNumber: serverPageNumber,
    pageSize: serverPageSize,
    totalPages: serverTotalPages,
    hasPreviousPage,
    hasNextPage
  } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getHeaderProps, getResizerFor } = useResizableColumns(['index', 'OrderNo', 'OpsCustomerName', 'WeaveCode', 'OpsSupplierName', 'ProductStockQuantity', 'RollCount', 'UnitName', 'StockPlace', 'Lot', 'Name', 'actions'], 'StockTable');

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    OrderNo: '',
    CustomerName: '',
    ProductCode: '',
    ProductStockQuantityWhole: '',
    ProductStockQuantityDecimal: '',
    RollCount: '',
    Lot: '',
    StockPlace: ''
  });

  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('InsertDatetime');

  const parseDisplayQuantity = (raw) => {
    if (raw === null || raw === undefined) return null;
    const normalized = String(raw).trim().replace(',', '.');
    if (normalized === '') return null;
    const value = Number(normalized);
    return Number.isNaN(value) ? null : value;
  };

  const formatDisplayQuantity = (raw) => {
    const value = parseDisplayQuantity(raw);
    if (value === null) return '';
    return value.toLocaleString('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const toScaledFilterQuantity = (whole, decimal) => {
    const wholeValue = String(whole || '').replace(/\D/g, '');
    const decimalValue = String(decimal || '').replace(/\D/g, '').slice(0, 2);

    if (!wholeValue && !decimalValue) {
      return null;
    }

    const wholePart = wholeValue ? parseInt(wholeValue, 10) : 0;
    const decimalPart = decimalValue ? parseInt(decimalValue.padEnd(2, '0'), 10) : 0;
    return (wholePart * 100) + decimalPart;
  };

  const handleDecimalInput = (e, field) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 2);
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleWholeInput = (e, field) => {
    const value = e.target.value.replace(/\D/g, '');
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handlePriceKeyDown = (e, decimalField) => {
    const isDotOrComma = e.key === '.' || e.key === ',' ||
      e.keyCode === 190 || e.keyCode === 110 || e.keyCode === 188;
    if (isDotOrComma) {
      e.preventDefault();
      const decimalInput = document.querySelector(`input[name="${decimalField}"]`);
      if (decimalInput) {
        decimalInput.focus();
        decimalInput.select();
      }
    }
  };

  // API'ye istek gönder
  const fetchStocks = useCallback((pageNum = 1, pageSz = rowsPerPage) => {
    const trimmedOrderNo = filters.OrderNo.trim();
    const trimmedCustomerName = filters.CustomerName.trim();
    const trimmedProductCode = filters.ProductCode.trim();
    const hasShortOrderNo = trimmedOrderNo.length > 0 && trimmedOrderNo.length < 3;
    const hasShortCustomerName = trimmedCustomerName.length > 0 && trimmedCustomerName.length < 3;
    const hasShortProductCode = trimmedProductCode.length > 0 && trimmedProductCode.length < 3;
    if (hasShortOrderNo || hasShortCustomerName || hasShortProductCode) {
      return;
    }

    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const sessionUserId = localStorage.getItem('sessionUserId');
    const userId = sessionUserId || userData?.sessionUserId;

    const payload = {
      SessionUserId: String(userId),
      PageNumber: pageNum,
      PageSize: pageSz,
      OrderBy: orderBy || 'InsertDatetime',
      IsDescending: order === 'desc',
    };

    // Filtreler
    if (trimmedOrderNo) {
      payload.OrderNo = trimmedOrderNo;
    }
    if (trimmedCustomerName) {
      payload.CustomerName = trimmedCustomerName;
    }
    if (trimmedProductCode) {
      payload.ProductCode = trimmedProductCode;
    }
    const normalizedStockQuantity = toScaledFilterQuantity(filters.ProductStockQuantityWhole, filters.ProductStockQuantityDecimal);
    if (normalizedStockQuantity !== null) {
      payload.ProductStockQuantity = normalizedStockQuantity;
    }
    const normalizedRollCount = String(filters.RollCount || '').trim();
    if (normalizedRollCount !== '') {
      const parsedRollCount = parseInt(normalizedRollCount, 10);
      if (!Number.isNaN(parsedRollCount)) {
        payload.RollCount = parsedRollCount;
      }
    }
    if (filters.Lot?.trim()) {
      payload.Lot = filters.Lot.trim();
    }
    if (filters.StockPlace?.trim()) {
      payload.StockPlace = filters.StockPlace.trim();
    }

    dispatch(getStockRequest(payload));
  }, [dispatch, filters, order, orderBy, rowsPerPage]);

  // Component mount'ta verileri çek
  useEffect(() => {
    fetchStocks(1, rowsPerPage);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Filtre değiştiğinde otomatik istek at (debounce ile)
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      fetchStocks(1, rowsPerPage);
    }, 400); // 400ms debounce

    return () => clearTimeout(timer);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sayfa Redux'dan geldiğinde local state'i güncelle
  useEffect(() => {
    if (serverPageNumber) setPage(serverPageNumber);
  }, [serverPageNumber]);

  const handleEdit = (item) => {
    navigate(`/stock/edit/${item.StockGuid}`, { state: { stock: item } });
  };

  const handleDeleteClick = (item) => {
    setDeleteModal({ open: true, item });
  };

  const handleDeleteConfirm = () => {
    if (deleteModal.item) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const sessionUserId = localStorage.getItem('sessionUserId');
      const userId = sessionUserId || userData?.sessionUserId;
      if (userId && userId !== 'undefined') {
        const deleteData = {
          SessionUserId: String(userId),
          StockId: deleteModal.item.StockGuid
        };
        deleteStockRequest(deleteData);
      }
    }
    setDeleteModal({ open: false, item: null });
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ open: false, item: null });
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
    fetchStocks(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (value) => {
    setRowsPerPage(value);
    setPage(1);
    fetchStocks(1, value);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    const clearedFilters = {
      OrderNo: '',
      CustomerName: '',
      ProductCode: '',
      ProductStockQuantityWhole: '',
      ProductStockQuantityDecimal: '',
      RollCount: '',
      Lot: '',
      StockPlace: ''
    };
    setFilters(clearedFilters);
    setPage(1);
    // Filtreleri temizledikten sonra yeni request
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const sessionUserId = localStorage.getItem('sessionUserId');
    const userId = sessionUserId || userData?.sessionUserId;
    dispatch(getStockRequest({
      SessionUserId: String(userId),
      PageNumber: 1,
      PageSize: rowsPerPage,
      OrderBy: orderBy || 'InsertDatetime',
      IsDescending: order === 'desc',
    }));
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleRequestSort = (property) => {
    const trimmedOrderNo = filters.OrderNo.trim();
    const trimmedCustomerName = filters.CustomerName.trim();
    const trimmedProductCode = filters.ProductCode.trim();
    const hasShortOrderNo = trimmedOrderNo.length > 0 && trimmedOrderNo.length < 3;
    const hasShortCustomerName = trimmedCustomerName.length > 0 && trimmedCustomerName.length < 3;
    const hasShortProductCode = trimmedProductCode.length > 0 && trimmedProductCode.length < 3;
    if (hasShortOrderNo || hasShortCustomerName || hasShortProductCode) {
      return;
    }

    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);
    // Yeni sıralama ile server'a istek gönder
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const sessionUserId = localStorage.getItem('sessionUserId');
    const userId = sessionUserId || userData?.sessionUserId;

    const payload = {
      SessionUserId: String(userId),
      PageNumber: 1,
      PageSize: rowsPerPage,
      OrderBy: property,
      IsDescending: newOrder === 'desc',
    };

    // Mevcut filtreleri ekle
    if (trimmedOrderNo) {
      payload.OrderNo = trimmedOrderNo;
    }
    if (trimmedCustomerName) {
      payload.CustomerName = trimmedCustomerName;
    }
    if (trimmedProductCode) {
      payload.ProductCode = trimmedProductCode;
    }
    const normalizedStockQuantity = toScaledFilterQuantity(filters.ProductStockQuantityWhole, filters.ProductStockQuantityDecimal);
    if (normalizedStockQuantity !== null) {
      payload.ProductStockQuantity = normalizedStockQuantity;
    }
    const normalizedRollCount = String(filters.RollCount || '').trim();
    if (normalizedRollCount !== '') {
      const parsedRollCount = parseInt(normalizedRollCount, 10);
      if (!Number.isNaN(parsedRollCount)) {
        payload.RollCount = parsedRollCount;
      }
    }
    if (filters.Lot?.trim()) {
      payload.Lot = filters.Lot.trim();
    }
    if (filters.StockPlace?.trim()) {
      payload.StockPlace = filters.StockPlace.trim();
    }

    setPage(1);
    dispatch(getStockRequest(payload));
  };

  const columns = [
    { id: 'index', label: '#', sortable: false, filterable: false },
    { id: 'OrderNo', label: t('orderNo') || 'Order No', sortable: false, filterable: 'like' },
    { id: 'OpsCustomerName', label: t('customerName') || 'Customer', sortable: false, filterable: false },
    { id: 'WeaveCode', label: t('productCode') || 'Weave Code', sortable: false, filterable: false },
    { id: 'OpsSupplierName', label: t('supplierName') || 'Supplier', sortable: false, filterable: false },
    { id: 'ProductStockQuantity', label: t('productStockQuantity') || 'Quantity', sortable: true, filterable: 'decimal' },
    { id: 'RollCount', label: t('stockRollCount') || 'Roll Count', sortable: false, filterable: 'integer' },
    { id: 'UnitName', label: t('unitName') || 'Unit Name', sortable: false, filterable: false },
    { id: 'StockPlace', label: t('stockPlace') || 'Stock Place', sortable: true, filterable: 'exact' },
    { id: 'Lot', label: t('lot') || 'Lot', sortable: true, filterable: 'exact' },
    { id: 'Name', label: t('stockStatus') || 'Stock Status', sortable: false, filterable: false },
    { id: 'actions', label: t('actions'), sortable: false, filterable: false }
  ];

  // Server'dan gelen veriyi doğrudan kullan (filtreleme artık sunucu tarafında)
  const filteredData = stockList;

  const exportColumns = useMemo(() => {
    return columns.filter(col => !['actions', 'index', 'expand'].includes(col.id));
  }, [columns]);

  const buildCellValue = (colId, item) => {
    switch (colId) {
      case 'ProductStockQuantity':
        return formatDisplayQuantity(item[colId]);
      case 'WeaveCode':
        // Display WeaveCode if exists, otherwise show YarnCode - YarnName
        return item.WeaveCode || (item.YarnCode && item.YarnName ? `${item.YarnCode} - ${item.YarnName}` : '');
      case 'OrderNo':
        return item.OrderNo || '';
      case 'RollCount':
        return item.RollCount || '';
      default:
        return item[colId] ?? '';
    }
  };

  // Server-side pagination: veriyi doğrudan kullan
  const getSortedData = () => {
    return filteredData;
  };

  const totalRows = totalCount;
  const totalPages = serverTotalPages;
  const paginationItems = [];
  const maxVisiblePages = 7;
  let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  if (startPage > 1) {
    paginationItems.push(
      <CPaginationItem key="first" onClick={() => handleChangePage(1)}>
        1
      </CPaginationItem>
    );

    if (startPage > 2) {
      paginationItems.push(
        <CPaginationItem key="start-ellipsis" disabled>
          ...
        </CPaginationItem>
      );
    }
  }

  for (let i = startPage; i <= endPage; i++) {
    paginationItems.push(
      <CPaginationItem key={i} active={i === page} onClick={() => handleChangePage(i)}>
        {i}
      </CPaginationItem>
    );
  }

  if (endPage < totalPages) {
    if (endPage < totalPages - 1) {
      paginationItems.push(
        <CPaginationItem key="end-ellipsis" disabled>
          ...
        </CPaginationItem>
      );
    }

    paginationItems.push(
      <CPaginationItem key="last" onClick={() => handleChangePage(totalPages)}>
        {totalPages}
      </CPaginationItem>
    );
  }

  const activeFilterCount = Object.entries(filters)
    .filter(([key, value]) => !['ProductStockQuantityWhole', 'ProductStockQuantityDecimal'].includes(key) && value && value.toString().trim() !== '')
    .length
    + ((String(filters.ProductStockQuantityWhole || '').trim() || String(filters.ProductStockQuantityDecimal || '').trim()) ? 1 : 0);

  return (
    <div className="stock-table-container">
      <CCard>
        <CCardBody>
          <div className="filter-controls mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <CButton
                  color={showFilters ? "primary" : "outline-primary"}
                  variant={showFilters ? "solid" : "outline"}
                  size="sm"
                  onClick={toggleFilters}
                  className="filter-toggle-btn"
                >
                  <CIcon icon={cilFilter} size="sm" className="me-1" />
                  {t('filters')}
                  {activeFilterCount > 0 && (
                    <span className="filter-count-badge ms-1">{activeFilterCount}</span>
                  )}
                </CButton>
                {activeFilterCount > 0 && (
                  <CButton
                    color="outline-secondary"
                    variant="outline"
                    size="sm"
                    onClick={clearFilters}
                    className="clear-filters-btn"
                  >
                    <CIcon icon={cilX} size="sm" className="me-1" />
                    {t('clearFilters')}
                  </CButton>
                )}
              </div>
              <div className="results-info d-flex align-items-center gap-2">
                <span className="text-muted">
                  {totalRows} {t('resultsFound')}
                </span>
                <ExportData
                  columns={exportColumns}
                  rows={getSortedData()}
                  allRows={stockList}
                  buildCellValue={buildCellValue}
                  filenamePrefix="stocks_export"
                  triggerDisabled={!filteredData || filteredData.length === 0}
                />
              </div>
            </div>
          </div>

          {/* Filtre Alanları */}
          <CCollapse visible={showFilters}>
            <div className="filter-panel mb-4">
              <CRow className="g-3">
                {/* OrderNo (minimum 3 karakter ile parçalı eşleşme) */}
                <CCol xs={12} sm={6} md={4} lg={3}>
                  <div className="filter-field">
                    <label className="filter-label">{t('orderNo') || 'Order No'}</label>
                    <div className="filter-input-wrapper">
                      <CIcon icon={cilSearch} className="filter-input-icon" />
                      <CFormInput
                        type="text"
                        placeholder={`${t('search')} ${(t('orderNo') || 'Order No').toLowerCase()}...`}
                        value={filters.OrderNo}
                        onChange={(e) => handleFilterChange('OrderNo', e.target.value)}
                        className="filter-input"
                        size="sm"
                      />
                      {filters.OrderNo && (
                        <CButton
                          color="link"
                          size="sm"
                          className="filter-clear-btn"
                          onClick={() => handleFilterChange('OrderNo', '')}
                        >
                          <CIcon icon={cilX} size="sm" />
                        </CButton>
                      )}
                    </div>
                  </div>
                </CCol>

                {/* CustomerName (minimum 3 karakter ile parçalı eşleşme) */}
                <CCol xs={12} sm={6} md={4} lg={3}>
                  <div className="filter-field">
                    <label className="filter-label">{t('customerName') || 'Müşteri Adı'}</label>
                    <div className="filter-input-wrapper">
                      <CIcon icon={cilSearch} className="filter-input-icon" />
                      <CFormInput
                        type="text"
                        placeholder={`${t('search')} ${(t('customerName') || 'müşteri adı').toLowerCase()}...`}
                        value={filters.CustomerName}
                        onChange={(e) => handleFilterChange('CustomerName', e.target.value)}
                        className="filter-input"
                        size="sm"
                      />
                      {filters.CustomerName && (
                        <CButton
                          color="link"
                          size="sm"
                          className="filter-clear-btn"
                          onClick={() => handleFilterChange('CustomerName', '')}
                        >
                          <CIcon icon={cilX} size="sm" />
                        </CButton>
                      )}
                    </div>
                  </div>
                </CCol>

                {/* ProductCode (minimum 3 karakter ile parçalı eşleşme) */}
                <CCol xs={12} sm={6} md={4} lg={3}>
                  <div className="filter-field">
                    <label className="filter-label">{t('productCode') || t('weaveCode') || 'Ürün Kodu'}</label>
                    <div className="filter-input-wrapper">
                      <CIcon icon={cilSearch} className="filter-input-icon" />
                      <CFormInput
                        type="text"
                        placeholder={`${t('search')} ${(t('productCode') || t('weaveCode') || 'ürün kodu').toLowerCase()}...`}
                        value={filters.ProductCode}
                        onChange={(e) => handleFilterChange('ProductCode', e.target.value)}
                        className="filter-input"
                        size="sm"
                      />
                      {filters.ProductCode && (
                        <CButton
                          color="link"
                          size="sm"
                          className="filter-clear-btn"
                          onClick={() => handleFilterChange('ProductCode', '')}
                        >
                          <CIcon icon={cilX} size="sm" />
                        </CButton>
                      )}
                    </div>
                  </div>
                </CCol>

                {/* ProductStockQuantity (decimal) */}
                <CCol xs={12} sm={6} md={4} lg={3}>
                  <div className="filter-field">
                    <label className="filter-label">{t('productStockQuantity') || 'Quantity'}</label>
                    <div className="decimal-input-group">
                      <CFormInput
                        type="text"
                        name="FilterProductStockQuantityWhole"
                        className="filter-input whole-input"
                        value={filters.ProductStockQuantityWhole}
                        onChange={(e) => handleWholeInput(e, 'ProductStockQuantityWhole')}
                        onKeyDown={(e) => handlePriceKeyDown(e, 'FilterProductStockQuantityDecimal')}
                        size="sm"
                        placeholder="0"
                      />
                      <span className="decimal-separator">,</span>
                      <CFormInput
                        type="text"
                        name="FilterProductStockQuantityDecimal"
                        className="filter-input decimal-input"
                        value={filters.ProductStockQuantityDecimal}
                        onChange={(e) => handleDecimalInput(e, 'ProductStockQuantityDecimal')}
                        size="sm"
                        placeholder="00"
                      />
                    </div>
                  </div>
                </CCol>

                {/* Lot (exact) */}
                <CCol xs={12} sm={6} md={4} lg={3}>
                  <div className="filter-field">
                    <label className="filter-label">{t('lot') || 'Lot'}</label>
                    <div className="filter-input-wrapper">
                      <CIcon icon={cilSearch} className="filter-input-icon" />
                      <CFormInput
                        type="text"
                        placeholder={`${t('search')} ${(t('lot') || 'Lot').toLowerCase()}...`}
                        value={filters.Lot}
                        onChange={(e) => handleFilterChange('Lot', e.target.value)}
                        className="filter-input"
                        size="sm"
                      />
                      {filters.Lot && (
                        <CButton
                          color="link"
                          size="sm"
                          className="filter-clear-btn"
                          onClick={() => handleFilterChange('Lot', '')}
                        >
                          <CIcon icon={cilX} size="sm" />
                        </CButton>
                      )}
                    </div>
                  </div>
                </CCol>

                {/* RollCount (exact integer) */}
                <CCol xs={12} sm={6} md={4} lg={3}>
                  <div className="filter-field">
                    <label className="filter-label">{t('stockRollCount') || 'Roll Count'}</label>
                    <div className="filter-input-wrapper">
                      <CIcon icon={cilSearch} className="filter-input-icon" />
                      <CFormInput
                        type="text"
                        placeholder={`${t('search')} ${(t('stockRollCount') || 'Roll Count').toLowerCase()}...`}
                        value={filters.RollCount}
                        onChange={(e) => handleWholeInput(e, 'RollCount')}
                        className="filter-input"
                        size="sm"
                      />
                      {filters.RollCount && (
                        <CButton
                          color="link"
                          size="sm"
                          className="filter-clear-btn"
                          onClick={() => handleFilterChange('RollCount', '')}
                        >
                          <CIcon icon={cilX} size="sm" />
                        </CButton>
                      )}
                    </div>
                  </div>
                </CCol>

                {/* StockPlace (exact) */}
                <CCol xs={12} sm={6} md={4} lg={3}>
                  <div className="filter-field">
                    <label className="filter-label">{t('stockPlace') || 'Stock Place'}</label>
                    <div className="filter-input-wrapper">
                      <CIcon icon={cilSearch} className="filter-input-icon" />
                      <CFormInput
                        type="text"
                        placeholder={`${t('search')} ${(t('stockPlace') || 'Stock Place').toLowerCase()}...`}
                        value={filters.StockPlace}
                        onChange={(e) => handleFilterChange('StockPlace', e.target.value)}
                        className="filter-input"
                        size="sm"
                      />
                      {filters.StockPlace && (
                        <CButton
                          color="link"
                          size="sm"
                          className="filter-clear-btn"
                          onClick={() => handleFilterChange('StockPlace', '')}
                        >
                          <CIcon icon={cilX} size="sm" />
                        </CButton>
                      )}
                    </div>
                  </div>
                </CCol>
              </CRow>
            </div>
          </CCollapse>

          <CTable hover responsive>
            <CTableHead>
              <CTableRow>
                {columns.map((column) => {
                  const headerProps = getHeaderProps(column.id);
                  return (
                    <CTableHeaderCell key={column.id} className="table-header-cell" style={{ cursor: column.sortable ? 'pointer' : 'default', paddingRight: '8px', ...(headerProps.style || {}) }} onClick={() => column.sortable && handleRequestSort(column.id)}>
                      {column.label}
                      {column.sortable && orderBy === column.id && (
                        <CIcon icon={order === 'asc' ? cilArrowTop : cilArrowBottom} size="sm" className="ms-1" />
                      )}
                      {getResizerFor(column.id)}
                    </CTableHeaderCell>
                  );
                })}
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {getSortedData().length > 0 ? (
                <>
                  {getSortedData().map((item, index) => (
                    <CTableRow key={item.StockGuid || index}>
                      <CTableDataCell>{(page - 1) * rowsPerPage + index + 1}</CTableDataCell>
                      <CTableDataCell>
                        <CBadge color="primary" shape="rounded-pill">
                          {item.OrderNo || '-'}
                        </CBadge>
                      </CTableDataCell>
                      <CTableDataCell>{item.OpsCustomerName || '-'}</CTableDataCell>
                      <CTableDataCell>
                        {item.WeaveCode || (item.YarnCode && item.YarnName ? `${item.YarnCode} - ${item.YarnName}` : '-')}
                      </CTableDataCell>
                      <CTableDataCell>{item.OpsSupplierName || '-'}</CTableDataCell>
                      <CTableDataCell>{formatDisplayQuantity(item.ProductStockQuantity)}</CTableDataCell>
                      <CTableDataCell>{item.RollCount || '-'}</CTableDataCell>
                      <CTableDataCell>{item.UnitName}</CTableDataCell>
                      <CTableDataCell>{item.StockPlace}</CTableDataCell>
                      <CTableDataCell>{item.Lot || '-'}</CTableDataCell>
                      <CTableDataCell>{item.Name}</CTableDataCell>
                      <CTableDataCell>
                        <div className="d-flex gap-2">
                          <CTooltip content={t('editItem')}>
                            <div className="cursor-pointer p-1" onClick={() => handleEdit(item)}>
                              <CIcon icon={cilPencil} size="sm" style={{ color: mainColors.primary }} />
                            </div>
                          </CTooltip>
                          <CTooltip content={t('deleteItem')}>
                            <div className="cursor-pointer p-1" onClick={() => handleDeleteClick(item)}>
                              <CIcon icon={cilTrash} size="sm" style={{ color: '#dc3545' }} />
                            </div>
                          </CTooltip>
                        </div>
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                  {activeFilterCount > 0 && (
                    <CTableRow>
                      <CTableDataCell />
                      <CTableDataCell />
                      <CTableDataCell />
                      <CTableDataCell />
                      <CTableDataCell />
                      <CTableDataCell>
                        <span className="text-muted">{t('total') || 'toplam'}: </span>
                        <strong>{formatDisplayQuantity(filteredData.reduce((sum, it) => sum + (parseDisplayQuantity(it.ProductStockQuantity) || 0), 0))}</strong>
                      </CTableDataCell>
                      <CTableDataCell />
                      <CTableDataCell />
                      <CTableDataCell />
                      <CTableDataCell />
                      <CTableDataCell />
                      <CTableDataCell />
                    </CTableRow>
                  )}
                </>
              ) : (
                <CTableRow>
                  <CTableDataCell colSpan={columns.length} className="text-center py-4">
                    <div className="no-data-message">
                      {isLoading ? (
                        <div className="loading-message">
                          <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                          {t('loadingData')}
                        </div>
                      ) : activeFilterCount > 0 ? (
                        <div className="no-results-message">
                          <CIcon icon={cilSearch} size="xl" className="mb-2 text-muted" />
                          <div>{t('noResultsFound')}</div>
                          <CButton
                            color="link"
                            size="sm"
                            onClick={clearFilters}
                            className="mt-2"
                          >
                            {t('clearFiltersToSeeAll')}
                          </CButton>
                        </div>
                      ) : (
                        <div className="text-muted">{t('noDataFound')}</div>
                      )}
                    </div>
                  </CTableDataCell>
                </CTableRow>
              )}
            </CTableBody>
          </CTable>
          {totalRows > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">{t('show')}:</span>
                <select className="form-select form-select-sm" value={rowsPerPage} onChange={(e) => handleChangeRowsPerPage(parseInt(e.target.value, 10))} style={{ width: 'auto' }}>
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-muted">{t('entries')}</span>
              </div>
              <CPagination className="mb-0">
                <CPaginationItem
                  aria-label={t('previous')}
                  onClick={() => page > 1 && handleChangePage(page - 1)}
                  disabled={page === 1}
                >
                  &laquo;
                </CPaginationItem>
                {paginationItems}
                <CPaginationItem
                  aria-label={t('next')}
                  onClick={() => page < totalPages && handleChangePage(page + 1)}
                  disabled={page === totalPages}
                >
                  &raquo;
                </CPaginationItem>
              </CPagination>
              <div className="text-muted">
                {t('showing')} {Math.min((page - 1) * rowsPerPage + 1, totalRows)} {t('to')}{' '}
                {Math.min(page * rowsPerPage, totalRows)} {t('of')} {totalRows} {t('entries')}
              </div>
            </div>
          )}
        </CCardBody>
      </CCard>
      <CModal visible={deleteModal.open} onClose={handleDeleteCancel}>
        <CModalHeader>
          <CModalTitle>{t('deleteItem')}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p>{t('confirmDelete')}</p>
          {deleteModal.item && (
            <div className="bg-light p-3 rounded">
              <strong>{t('unitName') || 'Unit Name'}:</strong> {deleteModal.item.UnitName}<br />
              <strong>{t('stockStatus') || 'Stock Status'}:</strong> {deleteModal.item.Name}<br />
              <strong>{t('productStockQuantity') || 'Quantity'}:</strong> {formatDisplayQuantity(deleteModal.item.ProductStockQuantity)}
            </div>
          )}
          <p className="text-danger mt-2 mb-0">
            <small>{t('thisActionCannotBeUndone')}</small>
          </p>
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={handleDeleteCancel}>{t('cancel')}</CButton>
          <CButton color="danger" onClick={handleDeleteConfirm}>{t('delete')}</CButton>
        </CModalFooter>
      </CModal>
    </div>
  );
}

const mapStateToProps = (state) => ({
  stockList: state.stockReducer.stockList || [],
  isLoading: state.stockReducer.isLoading,
  // Sayfalama alanları
  totalCount: state.stockReducer.totalCount || 0,
  pageNumber: state.stockReducer.pageNumber || 1,
  pageSize: state.stockReducer.pageSize || 10,
  totalPages: state.stockReducer.totalPages || 0,
  hasPreviousPage: state.stockReducer.hasPreviousPage || false,
  hasNextPage: state.stockReducer.hasNextPage || false,
});

const mapDispatchToProps = (dispatch) => ({
  deleteStockRequest: (payload) => dispatch(deleteStockRequest(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StockTable); 
